import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { PositionAnalysisResult } from '@/types/positionAnalysisTypes';
import { useState } from 'react';

interface PositionAnalysisDashboardProps {
  results: PositionAnalysisResult;
  onBack: () => void;
  onViewMemberProfile?: (memberId: string) => void;
}

export const PositionAnalysisDashboard = ({ results, onBack, onViewMemberProfile }: PositionAnalysisDashboardProps) => {
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());

  const toggleMemberDetails = (memberId: string) => {
    const newExpanded = new Set(expandedMembers);
    if (newExpanded.has(memberId)) {
      newExpanded.delete(memberId);
    } else {
      newExpanded.add(memberId);
    }
    setExpandedMembers(newExpanded);
  };

  const getFitColor = (fit: number) => {
    if (fit >= 80) return 'text-green-600';
    if (fit >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getFitBarColor = (fit: number) => {
    if (fit >= 80) return 'bg-green-500';
    if (fit >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      <Button
        onClick={onBack}
        variant="outline"
        className="h-10 px-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Builder
      </Button>

      <div className="bg-white rounded-lg border border-border p-6">
        <h2 className="text-xl font-semibold text-foreground mb-6">Company Analysis Results</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-border border-l-4 border-l-primary p-4">
            <p className="text-sm text-muted-foreground mb-2">Overall Company Fit</p>
            <p className={`text-4xl font-bold ${getFitColor(results.overallCompanyFit)}`}>
              {results.overallCompanyFit.toFixed(1)}%
            </p>
          </div>
          <div className="bg-white rounded-lg border border-border p-4">
            <p className="text-sm text-muted-foreground mb-2">Total Positions</p>
            <p className="text-4xl font-bold text-foreground">{results.positions.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-border p-4">
            <p className="text-sm text-muted-foreground mb-2">Total Members</p>
            <p className="text-4xl font-bold text-foreground">
              {results.positions.reduce((sum, pos) => sum + pos.members.length, 0)}
            </p>
          </div>
        </div>
      </div>

      {results.positions.map((position) => (
        <div key={position.positionId} className="bg-white rounded-lg border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{position.positionName}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Profile: {position.profileName}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">Average Fit</p>
                <p className={`text-2xl font-bold ${getFitColor(position.averageFit)}`}>
                  {position.averageFit.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {position.members.map((member) => (
              <div key={member.memberId} className="space-y-3 pb-3 border-b border-border last:border-b-0 last:pb-0">
                  <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground">{member.memberName}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {member.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-xl font-bold ${getFitColor(member.fitPercentage)}`}>
                        {member.fitPercentage.toFixed(1)}%
                      </p>
                    </div>
                    {onViewMemberProfile && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewMemberProfile(member.memberId)}
                        title="View Profile"
                        className="h-9 w-9 p-0 text-muted-foreground hover:text-foreground"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleMemberDetails(member.memberId)}
                      className="h-9 w-9 p-0 text-muted-foreground hover:text-foreground"
                    >
                      {expandedMembers.has(member.memberId) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-2.5 rounded-full transition-all ${getFitBarColor(member.fitPercentage)}`}
                    style={{ width: `${member.fitPercentage}%` }}
                  />
                </div>

                {expandedMembers.has(member.memberId) && (
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-medium text-foreground mb-3">Trait Breakdown</p>
                    <div className="space-y-2.5">
                      {member.traitBreakdown.map((trait) => (
                        <div key={trait.trait} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">{trait.trait}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground">
                              {trait.actual.toFixed(1)}%
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ideal: {trait.idealMin}-{trait.idealMax}%
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              trait.isInRange 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {trait.isInRange ? 'In Range' : 'Off'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};